// SELECTION DETAILS - everything about what is selected, and the full
// quantities, one level at a time (owner, 2026-09-20: "like the detail
// panel in TimelineViewer").
//
// What is taken from that panel: it tells you everything about the thing,
// and a parent shows its children's amounts added up. What is NOT taken:
// how it does it. That one reduces over every descendant record held in
// the browser - its own comment says 1.5 GB of JSON on SpaceX - which a
// tick of 1,049,917 elements cannot survive. Here the server groups over
// rows and answers in 245 ms whatever the size.
//
// A PANEL THAT PRINTS "Length 0" HAS SAID SOMETHING FALSE: it reads as "we
// measured it and there is none". Electrical Fixtures carries no length,
// area or volume at all; conduits carry 486,388 ft. So only the measures
// that have a value are shown, and the panel names them.
//
// THE COUNT IS IN ONE TREE. The two trees file the same elements
// differently - commit category "Electrical Fixtures" is 1,049,917 and the
// record category of the same name is 1,049,892, twenty-five filed
// elsewhere. So the panel says which tree it counted in, and when the
// selection's own count differs it says the difference rather than
// quietly showing the smaller number.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Empty, Progress, Spin, Switch, Table, Tag, Tooltip } from "antd";
import { getElement, getSelectionSummary } from "api/tracking/trackingElementsApi";
import { STATUS_COLOR, STATUS_LABEL, STATUS_ORDER } from "./trackingTheme";
import { popupInStage } from "./stagePopup";
import { DEFAULT_UNITS, DETAIL_DIMENSIONS, DETAIL_SUMS, compact, exact, unitOf } from "./quantityFormat";
import AssemblyGroups from "./AssemblyGroups";
import PartCategoryPicker from "./PartCategoryPicker";
import { PART_CATEGORY_TOTALS_RULE } from "./partCategoryRule";
import SelectionElementList from "./SelectionElementList";

const T = { muted: "#6B7280", ink: "#111827", line: "#E5E7EB" };
const n0 = (v) => Number(v || 0).toLocaleString();
const labelOf = (key) => (DETAIL_SUMS.find((c) => c.key === key) || { label: key }).label;

// THE TOGGLE'S SENTENCE, OUTSIDE THE COMPONENT: this file's sibling
// (ModelTracking) overflows eslint-plugin-react-hooks' path counter with a few
// inline ternaries and reports 29 false "hook called conditionally" errors that
// fail the build, so the branching stays out of the JSX here too.
// THE ONLY COPY. There were two - this one and an identical function in
// ModelTracking.jsx - and when the switch was renamed on 2026-09-29 only the
// ModelTracking copy was edited: the label changed, the sentence under it did
// not, and terser dropped the edited copy as dead code. The bundle then said
// "Select filtered only" over a sentence describing what the switch used to do.
// Caught by grepping the SERVED bundle for the new wording and finding none.
//
// Says what the switch does now: it decides whether a person can select
// anything or only what the filter matched. It is not a visibility control in
// its own right - hiding is simply how the selection gate is enforced, because
// hidden elements are not clickable.
function filteredToggleLine(hasFilter, on) {
  if (!hasFilter) {
    return on
      ? "No quantity tracking filter is set, so everything is selectable. Filter the quantities and only those elements will be."
      : "No quantity tracking filter is set, so everything is selectable.";
  }
  if (on) return "Only the elements matching the quantity tracking filter are shown, so they are the only ones you can select in 3D.";
  return "Every element stays in the model and can be selected. The filter narrows the table and its numbers, not the 3D view.";
}
const TOGGLE_BG = { on: "#FFFDF5", off: "transparent" };

export default function SelectionDetails({
  // Bumped by every status write on the page: the per-status counts move.
  refreshKey = 0,
  filteredOnly = true,
  hasQuantityFilter = false,
  onFilteredOnly = null,
  projectId,
  selection = null,          // { nodeKeys } | { recordNodes } | { uniqueIds }
  selectionCount = null,     // what the bulk bar says, so the two can be compared
  // MID-DRAG: how many the rectangle has caught SO FAR (Paul's stream,
  // 2026-09-24). A number and nothing else - the identity has not been
  // sent yet, deliberately, so there is nothing to list and the panel
  // must not pretend otherwise. Null again the moment the drag ends.
  liveCount = null,
  // False when something else already owns the selection - today, only
  // select-all-filtered. The count is still shown, because the person is
  // dragging and deserves to see it working; the PROMISE is what
  // changes, because releasing will not replace what the panel shows.
  liveCountApplies = true,
  documentIds = [],
  units = DEFAULT_UNITS,
  height = 460,
  // The page has already asked for this selection - the same call that
  // feeds the bulk bar's count. Taking it rather than asking again is
  // what makes "one number in every place" true by construction.
  //
  // THREE VALUES, AND THEY MEAN THREE DIFFERENT THINGS - absent is not
  // null, which is the distinction this panel got wrong:
  //   undefined  nobody is supplying one; THIS PANEL OWNS THE FETCH
  //   null       the page owns it and has not answered YET - wait
  //   an object  the answer
  // It defaulted to null, so "the page has not answered yet" read as
  // "nobody will answer" and the panel fetched anyway - a SECOND call to
  // selection/summary about 1 ms after the page's own, from two call
  // sites that did not know about each other. Paul measured it on prod:
  // 80 calls against 18 page loads, on the endpoint whose worst call was
  // 45 SECONDS. The fix is not a cache; it is telling the two states
  // apart.
  summary: given = undefined,
  quantitiesState = "",
  // THE LEVEL ROWS ARE SELECTABLE (owner, 2026-09-20). Ticking one picks
  // that level's elements out of THE CURRENT SELECTION - it is a
  // HIGHLIGHT inside the set, never a change to what is drawn, so
  // unticking a level can never make geometry disappear. The page owns
  // the pick, because the viewer and the quantities tree have to see the
  // same one.
  levelPick = [],
  onLevelPick = null,
  levelPickBusy = false,
  levelPickNote = "",
  // WHAT THE SELECTION IS MADE OF (owner, 2026-09-24). The page asks for
  // this the same way it asks for `summary` - the panel does not fetch,
  // because reducing over records in the browser is the 1.5 GB path this
  // file exists to avoid. Absent, the section is not rendered at all, so
  // a page that knows nothing about parts is unchanged.
  assembly = null,
  assemblyLoading = false,
  // How a derived category meets its parent's totals, in the words of
  // whoever owns totals. Without it AssemblyGroups refuses to create -
  // see its header for why that is enforced rather than promised.
  // THE TOTALS RULE DEFAULTS TO THE RULE, not to the empty string.
  //
  // It defaulted to "" from September to 2026-10-02 and nothing ever
  // passed it, so AssemblyGroups' `canCreate` was false and the button
  // under "What it is made of" carried a tooltip saying how a derived
  // category meets its parent's totals had not been settled. IT IS
  // SETTLED NOW - partCategoryRule, agreed by the owner today - so that
  // tooltip was a message stating something untrue about the product, and
  // it is the third of those found today.
  //
  // DEFAULTING IT HERE RATHER THAN PASSING IT FROM ModelTracking is the
  // point: a prop that every caller must remember to pass is exactly how
  // this was missed for six weeks, and the new picker and this panel must
  // not be able to disagree about whether creating is allowed. A caller
  // may still override it - a surface with a different rule can say so -
  // but nobody has to know to.
  assemblyTotalsNote = PART_CATEGORY_TOTALS_RULE,
  onCreateSubcategory = null,
  // CUSTOM PART CATEGORIES, FROM A DROPDOWN, WITH NOTHING SELECTED
  // (owner, 2026-10-02). The section below needs no selection at all,
  // which is the whole point of it: "What it is made of" above can only
  // show the parts of what is already picked, so a category you have not
  // selected was unreachable.
  trackedCategories = [],
  onLoadPartGroups = null,
  onPreviewPartCategory = null,
  partStatusMode = "",
  canEdit = false,
  sourceCategory = "",
  // THE BOX, BY REFERENCE (owner, 2026-09-24). The id of the selection
  // document the viewer created when the rectangle was released. The
  // per-element list pages through it on the server instead of holding
  // 41,204 identifiers in this page to draw 500 rows.
  selectionId = null,
  onStatusWritten = null,
}) {
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [element, setElement] = useState(null);
  const [levels, setLevels] = useState(null);
  const [drill, setDrill] = useState([]);      // the path clicked into, for going back
  // CLOSED BY DEFAULT. The Elements list below is where status gets
  // assigned, constantly; a section somebody opens occasionally must not
  // push it off the screen. Same reasoning as AssemblyGroups shipping
  // closed.
  const [partsOpen, setPartsOpen] = useState(false);
  const seq = useRef(0);
  const docs = useMemo(() => (documentIds || []).join(","), [documentIds]);

  // What is being summarised: the page's selection, or a child of it that
  // was clicked into. DRILLED IN, this panel asks its own questions; at
  // the top it uses the page's answer - the same call the bulk bar's count
  // comes from, which is what makes the two numbers one number rather
  // than two that agree.
  const drilled = drill.length > 0;
  const asked = drilled ? drill[drill.length - 1].selection : selection;
  const askedKey = JSON.stringify(asked || null);

  useEffect(() => { setDrill([]); }, [JSON.stringify(selection || null)]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let alive = true;
    setLevels(null);
    if (!projectId || !asked) { setSummary(null); setElement(null); setError(""); return undefined; }
    // The page owns this one and has answered.
    if (!drilled && given) { setSummary(given); setError(""); setBusy(false); }
    // The page owns it and has NOT answered yet: wait for it rather than
    // asking the same question a millisecond later.
    if (!drilled && given === null) { setBusy(true); return undefined; }
    const mine = (seq.current += 1);
    // One call per SETTLED selection - the same 120 ms the bulk bar waits,
    // so ticking twelve boxes asks once. The last answer stays on screen
    // while the next is worked out; blanking reads as "there is nothing".
    const t = setTimeout(() => {
      if (!drilled && given !== undefined) {
        // Still ask for the element's own row when exactly one is picked -
        // the page's summary carries the numbers, not the properties.
        const only = asked.uniqueIds && asked.uniqueIds.length === 1 ? asked.uniqueIds[0] : null;
        if (!only) { setElement(null); return; }
      }
      setBusy(true);
      setError("");
      if (drilled || given === undefined) getSelectionSummary(projectId, { ...asked, documentIds: docs, selectionCount })
        .then((r) => {
          if (!alive || seq.current !== mine) return;
          setSummary(r.summary || null);
        })
        .catch((e) => { if (alive && seq.current === mine) { setError(e.message || "Could not read the selection"); setSummary(null); } })
        .finally(() => { if (alive && seq.current === mine) setBusy(false); });
      else setBusy(false);
      // One element: its own row in full, which is the "everything about
      // it" half of what he asked for.
      const one = asked.uniqueIds && asked.uniqueIds.length === 1 ? asked.uniqueIds[0] : null;
      if (one) {
        getElement(projectId, one).then((r) => { if (alive && seq.current === mine) setElement(r.element || null); })
          .catch(() => { if (alive && seq.current === mine) setElement(null); });
      } else setElement(null);
    }, 120);
    return () => { alive = false; clearTimeout(t); };
  }, [projectId, askedKey, docs, selectionCount, drilled, given, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const openLevels = useCallback(async () => {
    if (!asked) return;
    setLevels("loading");
    try {
      const r = await getSelectionSummary(projectId, { ...asked, documentIds: docs, levels: 1 });
      setLevels((r.summary && r.summary.levels) || []);
    } catch (e) { setLevels([]); }
  }, [projectId, asked, docs]);

  // WHILE THE BOX IS BEING DRAWN, the panel says what is happening
  // rather than presenting the previous selection as though it were
  // still the answer. It does NOT clear what it is showing: the identity
  // of the new selection has deliberately not been sent yet, and
  // blanking would read as "nothing is selected" when the truth is "we
  // do not know yet" - the same absence-as-a-value mistake we have now
  // fixed at three layers.
  const dragging = liveCount !== null;

  // THE SWITCH IS A MODE, NOT A PROPERTY OF A SELECTION, so it renders even
  // with nothing selected. It decides whether a person may select anything or
  // only what the filter matched - so hiding it until they have selected
  // something asks them to select before they can choose what is selectable
  // (owner, 2026-09-29: "i want the switch to be on by default in the
  // selection panel").
  const filteredOnlyRow = onFilteredOnly ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", marginBottom: 8, border: `1px solid ${T.line}`, borderRadius: 6, background: TOGGLE_BG[filteredOnly ? "on" : "off"] }}>
          {/* NOT disabled without a filter: with no filter there is nothing to
              narrow to, and the line below says so. Disabling it made the
              control look broken at the moment a person went looking for it. */}
          <Switch size="small" checked={filteredOnly} onChange={onFilteredOnly} />
          <div style={{ fontSize: 12 }}>
            <b>Select filtered only</b>
            <div style={{ color: T.muted }}>{filteredToggleLine(hasQuantityFilter, filteredOnly)}</div>
          </div>
        </div>
  ) : null;

  // THE CUSTOM PART CATEGORY SECTION IS NOT ABOUT THE SELECTION, which is
  // why it is built here and rendered in BOTH branches below.
  //
  // I SHIPPED IT INSIDE THE SELECTION BRANCH AND IT WAS THEREFORE
  // UNREACHABLE WITH NOTHING SELECTED - behind the exact gate it exists to
  // bypass. The owner's words were "from a dropdown list of all tracked
  // categories"; the whole point is that you do not have to pick elements
  // first. Found by a test that rendered this panel with no selection,
  // which is a thing none of my other tests did.
  const partCategoriesSection = onLoadPartGroups ? (
    <div style={{ marginTop: 12, borderTop: `1px solid ${T.line}`, paddingTop: 8 }}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setPartsOpen((v) => !v)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setPartsOpen((v) => !v); }}
        style={{ fontWeight: 600, fontSize: 12.5, marginBottom: 4, cursor: "pointer", userSelect: "none" }}
      >
        {partsOpen ? "\u25be" : "\u25b8"} Custom part categories
      </div>
      {partsOpen ? (
        <PartCategoryPicker
          projectId={projectId}
          categories={trackedCategories}
          documentIds={documentIds}
          loadGroups={onLoadPartGroups}
          onCreate={onCreateSubcategory}
          onPreview={onPreviewPartCategory}
          statusMode={partStatusMode}
          canEdit={canEdit}
        />
      ) : null}
    </div>
  ) : null;

  if (!selection && !dragging) {
    return (
      <div style={{ padding: 16 }}>
        {filteredOnlyRow}
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={
          <span style={{ fontSize: 12, color: T.muted }}>Tick something in a tree, or click an element in the model, and everything about it appears here.</span>
        } />
        {/* AND THE ONE THING ON THIS PANEL THAT NEEDS NO SELECTION. */}
        {partCategoriesSection}
      </div>
    );
  }

  // `s` is null when a drag has started with nothing selected before it:
  // there is a live count to show and no summary yet, which is a real
  // state and not an error.
  // Drilled in, the panel's own answer; at the top, the page's when it
  // has one. `given` undefined means nobody is supplying one, so the
  // panel's own answer is all there is.
  const s = drilled ? summary : (given || summary);
  const amountRows = s ? (s.measures || []).map((k) => ({ key: k, label: labelOf(k), value: s.amounts[k], unit: unitOf(DETAIL_SUMS.find((c) => c.key === k) || {}, units) })) : [];
  const installed = s && s.byStatus && s.byStatus.installed ? s.byStatus.installed.n : 0;
  const pct = s && s.elements ? Math.round((installed / s.elements) * 1000) / 10 : 0;
  // The two counts, when they are not the same number.
  const mismatch = s && Number.isFinite(selectionCount) && selectionCount !== null && s.elements !== selectionCount;

  return (
    <div style={{ maxHeight: height, overflowY: "auto", padding: "2px 2px 8px" }}>
      {/* SHOW ONLY FILTERED, AT THE TOP OF THE PANEL (owner, 2026-09-28).
          Off, this panel is what he picked in the model - clicks and
          ctrl-clicks, live. On, it is the quantity tracking filter's set.
          THE LINE UNDER IT SAYS WHICH, because a count on its own is not a
          statement of scope: 41,419 was always a real number and what it
          counted was never said, which is how a filter passed for a
          selection for four days. */}
      {filteredOnlyRow}
      {/* THE RECTANGLE, WHILE IT IS STILL BEING DRAWN. A count and the
          reason there is nothing else yet - not a spinner, which would
          say "working" when the truth is "still being chosen", and not a
          blank, which would say "nothing". What is below stays put until
          the drag ends and the real selection replaces it. */}
      {dragging ? (
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, padding: "6px 8px", marginBottom: 8, borderRadius: 6, background: "#E6F4FF" }}>
          <b style={{ fontSize: 16 }}>{n0(liveCount)}</b>
          <span style={{ fontSize: 12, color: T.muted }}>
            {liveCountApplies
              ? `element${liveCount === 1 ? "" : "s"} in the box so far - release to see them`
              : `element${liveCount === 1 ? "" : "s"} in the box - select-all-filtered is on, so releasing will not change what is below`}
          </span>
        </div>
      ) : null}
      {drill.length ? (
        <div style={{ marginBottom: 6, fontSize: 12 }}>
          <Button size="small" type="link" style={{ padding: 0 }} onClick={() => setDrill(drill.slice(0, -1))}>← back</Button>
          <span style={{ color: T.muted, marginLeft: 6 }}>{drill.map((d) => d.label).join(" › ")}</span>
        </div>
      ) : null}

      {error ? <div style={{ fontSize: 12, color: "#b42318", marginBottom: 8 }}>{error}</div> : null}
      {!s && busy ? <div style={{ padding: 16, textAlign: "center" }}><Spin size="small" /></div> : null}

      {s ? (
        <>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
            <span style={{ fontSize: 18, fontWeight: 700, color: T.ink }}>{n0(s.elements)}</span>
            <span style={{ fontSize: 12, color: T.muted }}>element{s.elements === 1 ? "" : "s"}</span>
            {busy ? <Spin size="small" /> : null}
            <Tooltip getPopupContainer={popupInStage} title={s.tree === "record" ? "Counted in the record tree (Tracking quantities)" : s.tree === "commit" ? "Counted in the model tree (Model elements)" : "The elements you picked"}>
              <Tag style={{ marginLeft: "auto" }}>{s.tree === "record" ? "record tree" : s.tree === "commit" ? "model tree" : "picked"}</Tag>
            </Tooltip>
          </div>

          {mismatch ? (
            <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 8, lineHeight: 1.35 }}>
              The selection stands for {n0(selectionCount)} element{selectionCount === 1 ? "" : "s"} in the
              {" "}{s.tree === "record" ? "model" : "record"} tree. The two trees file {n0(Math.abs(selectionCount - s.elements))}
              {" "}of them under different names - the same elements, counted in a different place.
            </div>
          ) : null}

          {/* Status, and what share is installed. */}
          <div style={{ border: `1px solid ${T.line}`, borderRadius: 6, padding: "8px 10px", marginBottom: 10 }}>
            <Progress percent={pct} size="small" strokeColor={STATUS_COLOR.installed} format={(p) => `${p}%`} />
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 4 }}>
              {STATUS_ORDER.map((k) => (
                <span key={k} style={{ fontSize: 11.5, color: T.muted, display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 9, height: 9, borderRadius: 2, background: STATUS_COLOR[k] }} />
                  {STATUS_LABEL[k]} {n0((s.byStatus[k] || {}).n)}
                </span>
              ))}
            </div>
          </div>

          {/* The amounts - only the ones that have a value. */}
          <div style={{ fontWeight: 600, fontSize: 12.5, marginBottom: 4 }}>Quantities</div>
          {amountRows.length ? (
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 4 }}>
              <tbody>
                {amountRows.map((r) => (
                  <tr key={r.key}>
                    <td style={{ padding: "3px 6px", borderTop: `1px solid ${T.line}`, fontSize: 12, color: T.muted }}>{r.label}</td>
                    <td style={{ padding: "3px 6px", borderTop: `1px solid ${T.line}`, fontSize: 12.5, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      <Tooltip getPopupContainer={popupInStage} title={exact(r.value, r.unit)}>{compact(r.value)}{r.unit ? ` ${r.unit}` : ""}</Tooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 6 }}>
              Nothing here carries a measured amount - these elements are counted, not measured.
            </div>
          )}
          {amountRows.length ? (
            <div style={{ fontSize: 11, color: T.muted, marginBottom: 10 }}>
              Showing {amountRows.map((r) => r.label.toLowerCase()).join(", ")} - the measures these elements carry. The rest are not measured on them.
            </div>
          ) : null}

          {/* One level down, and clickable: that is the "recursively". */}
          {(s.children || []).length ? (
            <>
              <div style={{ fontWeight: 600, fontSize: 12.5, margin: "6px 0 4px" }}>
                {s.children[0].kind === "family" ? "By family" : s.children[0].kind === "type" ? "By type" : "By group"}
                <span style={{ color: T.muted, fontWeight: 400 }}> · {s.children.length} </span>
              </div>
              <Table
                size="small" rowKey={(r) => r.value} pagination={false} dataSource={s.children}
                onRow={(r) => ({ onClick: () => drillInto(r), style: { cursor: "pointer" } })}
                columns={[
                  { title: "Name", dataIndex: "label", ellipsis: true },
                  { title: "Elements", dataIndex: "n", width: 90, align: "right", render: (v) => n0(v) },
                  ...(s.measures.includes("length") ? [{
                    title: `Length ${units.length}`, key: "len", width: 90, align: "right",
                    render: (_, r) => <Tooltip getPopupContainer={popupInStage} title={exact(r.amounts.length, units.length)}>{compact(r.amounts.length)}</Tooltip>,
                  }] : []),
                ]}
              />
            </>
          ) : null}

          {/* By level - the question the field asks, and the costly one. */}
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "10px 0 4px" }}>
            <div style={{ fontWeight: 600, fontSize: 12.5 }}>By level</div>
            {onLevelPick && levelPick.length ? (
              <>
                <span style={{ fontSize: 11.5, color: T.muted }}>
                  {levelPickBusy ? "working out the elements…" : (levelPickNote || `${levelPick.length} level${levelPick.length === 1 ? "" : "s"} picked - highlighted in the model`)}
                </span>
                <Button size="small" type="link" style={{ padding: 0 }} onClick={() => onLevelPick([])}>Clear</Button>
              </>
            ) : null}
          </div>
          {levels === null ? (
            <Button size="small" onClick={openLevels}>Work out the levels</Button>
          ) : levels === "loading" ? (
            <Spin size="small" />
          ) : levels.length ? (
            <Table
              size="small" rowKey="level" pagination={false} dataSource={levels}
              // Ticking a level asks the server for that level's elements
              // WITHIN this selection; the page paints them. Without
              // `onLevelPick` the table is exactly what it was.
              rowSelection={onLevelPick ? {
                selectedRowKeys: levelPick,
                onChange: (keys) => onLevelPick(keys.map(String)),
                columnWidth: 32,
              } : undefined}
              columns={[
                { title: "Level", dataIndex: "level", ellipsis: true },
                { title: "Elements", dataIndex: "n", width: 90, align: "right", render: (v) => n0(v) },
                ...(s.measures.includes("length") ? [{
                  title: `Length ${units.length}`, key: "len", width: 90, align: "right",
                  render: (_, r) => <Tooltip getPopupContainer={popupInStage} title={exact(r.amounts.length, units.length)}>{compact(r.amounts.length)}</Tooltip>,
                }] : []),
              ]}
            />
          ) : (
            <div style={{ fontSize: 11.5, color: T.muted }}>No level is recorded on these elements.</div>
          )}
        </>
      ) : null}

      {/* WHAT IT IS MADE OF - the assembly parts across the selection,
          and the button that tracks one of them as its own category. */}
      {assembly || assemblyLoading ? (
        <div style={{ marginTop: 12, borderTop: `1px solid ${T.line}`, paddingTop: 8 }}>
          <div style={{ fontWeight: 600, fontSize: 12.5, marginBottom: 4 }}>What it is made of</div>
          <AssemblyGroups
            payload={assembly}
            loading={assemblyLoading}
            sourceCategory={sourceCategory}
            units={units}
            totalsNote={assemblyTotalsNote}
            statusMode={partStatusMode}
            onPreview={onPreviewPartCategory}
            onCreate={canEdit ? onCreateSubcategory : null}
          />
        </div>
      ) : null}

      {/* THE ELEMENTS THEMSELVES, one row each, with a tick and a status
          control (owner, 2026-09-24). Drilled into a child the rows
          would no longer be the selection the button writes to, so the
          list belongs to the top level only. */}
      {!drilled ? (
        <SelectionElementList
          projectId={projectId}
          // SELECT-ALL-FILTERED CARRIES THE FILTER AND NOTHING ELSE - no
          // selection document, no ids (Paul, 2026-09-24). It is already
          // on the selection the page hands us, so the list reads it
          // from there and the page needs to pass nothing new. Rows
          // ticked in a filtered table arrive as the filter AND the ids,
          // which is "those rows as shown", and the server narrows by
          // both through the same query.
          filters={(asked && asked.filters) || null}
          selectionId={selectionId}
          uniqueIds={selectionId ? null : ((asked && asked.uniqueIds) || null)}
          documentIds={documentIds}
          onChanged={onStatusWritten}
        />
      ) : null}

      {/* One element: everything the model and the field know about it. */}
      {element ? (
        <div style={{ marginTop: 12, borderTop: `1px solid ${T.line}`, paddingTop: 8 }}>
          <div style={{ fontWeight: 600, fontSize: 12.5, marginBottom: 4 }}>This element</div>
          <Field label="Category" value={element.category} />
          <Field label="Family" value={element.family} />
          <Field label="Type" value={element.typeName} />
          <Field label="Name" value={element.name} />
          <Field label="Level" value={element.level} />
          <Field label="Status" value={STATUS_LABEL[element.status] || element.status} />
          <Field label="Status date" value={element.statusDate ? String(element.statusDate).slice(0, 10) : ""} />
          <Field label="In lists" value={(element.lists || []).map((l) => l.name).join(", ")} />
          <Field label="Unique id" value={element.uniqueId} mono />
          {DETAIL_DIMENSIONS.some((d) => element.measures && element.measures[d.key]) ? (
            <div style={{ marginTop: 6 }}>
              <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 2 }}>Its own dimensions</div>
              {DETAIL_DIMENSIONS.filter((d) => element.measures && element.measures[d.key]).map((d) => (
                <Field key={d.key} label={d.label} value={`${compact(element.measures[d.key])} ${unitOf(d, units)}`} />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* A CUSTOM CATEGORY OF PARTS, PICKED FROM A DROPDOWN (owner,
          2026-10-02: "allow in the right panel, to add a custom category
          for tracking, that counts assembly parts, from a dropdown list
          of all tracked categories, until we get to the assembly parts").
          Built above and rendered in BOTH branches of this component,
          because it is NOT about the current selection - and the first
          version of it lived only here, behind the early return, which
          made it unreachable with nothing selected: behind the very gate
          it exists to bypass. */}
      {partCategoriesSection}
    </div>
  );

  function drillInto(row) {
    // Into a child, in the same tree's words - so the numbers below are
    // the same numbers, asked one level down.
    const base = asked || {};
    let next = null;
    let label = row.label;
    if (row.kind === "family" && base.nodeKeys && base.nodeKeys.length === 1) {
      next = { nodeKeys: [`family:${base.nodeKeys[0].slice("category:".length)}|${row.value}`] };
    } else if (row.kind === "type" && base.nodeKeys && base.nodeKeys.length === 1) {
      next = { nodeKeys: [`type:${base.nodeKeys[0].slice("family:".length)}|${row.value}`] };
    } else if (row.kind === "group" && base.recordNodes && base.recordNodes.length === 1) {
      next = { recordNodes: [{ category: base.recordNodes[0].category, group: row.value }] };
    }
    if (next) setDrill([...drill, { label, selection: next }]);
  }
}

function Field({ label, value, mono = false }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div style={{ display: "flex", gap: 8, fontSize: 12, padding: "2px 0" }}>
      <span style={{ color: T.muted, minWidth: 92 }}>{label}</span>
      <span style={{ color: T.ink, wordBreak: "break-all", fontFamily: mono ? "monospace" : undefined, fontSize: mono ? 11 : 12 }}>{value}</span>
    </div>
  );
}
